/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useRef } from 'react'
import { CircularProgress } from '@material-ui/core'
import { Text } from '@tremor/react'

import moment from 'config/moment'
import { useApiCall } from 'hooks/useApi'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { MetricCard } from '../../components/MetricCard'
import { CustomCheckbox } from '../../components/CustomCheckbox'
import { getParticipationByLevel, ParticipationLevelData, ParticipationByLevelParams } from './api'
import { montserratStyle } from '../../styles'

interface ActiveUsersByLevelProps {
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  period: { month: number; year: number }
  // eslint-disable-next-line react/require-default-props
  storeId?: number
  compara: boolean
  // eslint-disable-next-line react/require-default-props
  compareStoreId?: number
  trigger: number
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

const ActiveUsersByLevel: React.FC<ActiveUsersByLevelProps> = ({
  period,
  storeId,
  compara,
  compareStoreId,
  trigger,
  onLoaded,
}) => {
  const [showAllUsers, setShowAllUsers] = useState(false)
  const [showRecurrent, setShowRecurrent] = useState(true)
  const [data, setData] = useState<ParticipationLevelData[] | null>(null)
  const [data2, setData2] = useState<ParticipationLevelData[] | null>(null)
  const canFetchRef = useRef(false)
  const skipFilterFetchRef = useRef(true)
  const [statsCall, statsLoading] = useApiCall<
    ParticipationByLevelParams,
    { data: ParticipationLevelData[] }
  >(getParticipationByLevel)

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading, componentId: 'ActiveUsersByLevel' })

  const fetchData = async (callOnLoaded = false) => {
    try {
      const startDate = moment()
        .year(period.year)
        .month(period.month)
        .startOf('month')
        .format('YYYY-MM-DD')
      const endDate = moment()
        .year(period.year)
        .month(period.month)
        .endOf('month')
        .format('YYYY-MM-DD')
      const userType = showRecurrent ? 'recurrent' : 'all'
      const response1 = await statsCall({
        startDate,
        endDate,
        storeId,
        userType,
      })
      if (response1?.data) {
        setData(response1.data)
      }
      if (compara) {
        const response2 = await statsCall({
          startDate,
          endDate,
          storeId: compareStoreId,
          userType,
        })
        if (response2?.data) {
          setData2(response2.data)
        }
      } else {
        setData2(null)
      }
    } catch (error) {
      setData(null)
      setData2(null)
    } finally {
      if (callOnLoaded && onLoaded) {
        onLoaded()
      }
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      canFetchRef.current = true
      fetchData(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  useEffect(() => {
    if (skipFilterFetchRef.current) {
      skipFilterFetchRef.current = false
      return
    }
    if (!canFetchRef.current) return
    fetchData(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRecurrent, showAllUsers])

  const handleAllUsersChange = (checked: boolean) => {
    if (checked) {
      setShowAllUsers(true)
      setShowRecurrent(false)
    } else {
      // Si intenta desmarcar, asegurar que el otro esté marcado
      if (!showRecurrent) {
        setShowRecurrent(true)
      }
      setShowAllUsers(false)
    }
  }

  const handleRecurrentChange = (checked: boolean) => {
    if (checked) {
      setShowRecurrent(true)
      setShowAllUsers(false)
    } else {
      // Si intenta desmarcar, asegurar que el otro esté marcado
      if (!showAllUsers) {
        setShowAllUsers(true)
      }
      setShowRecurrent(false)
    }
  }

  const calculateDiff = (current: number, previous: number) => {
    if (previous === 0) return 0
    return ((current - previous) / previous) * 100
  }

  if (statsLoading || !data) {
    return (
      <div className="bg-white rounded-lg shadow p-6 flex flex-col w-full">
        <div className="flex flex-row justify-between items-center mb-4">
          <span className="text-base font-bold" style={montserratStyle}>
            Usuarios activos por nivel: nuevos y recurrentes
          </span>
        </div>
        <div className="flex items-center justify-center h-32">
          {statsLoading ? (
            <CircularProgress size={40} />
          ) : (
            <Text>No hay datos disponibles para mostrar en este momento.</Text>
          )}
        </div>
      </div>
    )
  }

  // Formatear datos para mostrar dos tarjetas por nivel: Visitantes y Visitas
  const displayData = data.flatMap((level, idx) => {
    const prev = data2 ? data2[idx] : undefined
    return [
      {
        title: `${level.nivel}`,
        subtitle: 'Visitantes',
        value: level.visitors,
        prev: prev ? prev.visitors : 0,
        diff: prev ? calculateDiff(level.visitors, prev.visitors) : 0,
        isPositive: !prev || level.visitors >= prev.visitors,
        isPercent: true,
      },
      {
        title: `${level.nivel}`,
        subtitle: 'Visitas',
        value: level.visits,
        prev: prev ? prev.visits : 0,
        diff: prev ? calculateDiff(level.visits, prev.visits) : 0,
        isPositive: !prev || level.visits >= prev.visits,
        isPercent: true,
      },
    ]
  })

  // Agrupar displayData por nivel
  const groupedByLevel = []
  for (let i = 0; i < displayData.length; i += 2) {
    groupedByLevel.push(displayData.slice(i, i + 2))
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 flex flex-col w-full mt-8">
      <div className="flex flex-row justify-between items-center mb-4">
        <span className="text-base font-bold" style={montserratStyle}>
          Usuarios activos por nivel: nuevos y recurrentes
        </span>
        <div className="flex gap-6 items-center">
          <label className="flex items-center gap-2 text-xs font-medium">
            <CustomCheckbox
              checked={showAllUsers}
              onChange={e => handleAllUsersChange(e.target.checked)}
              className="w-4 h-4 rounded border border-gray-400"
            />
            Todos los usuarios
          </label>
          <label className="flex items-center gap-2 text-xs font-medium">
            <CustomCheckbox
              checked={showRecurrent}
              onChange={e => handleRecurrentChange(e.target.checked)}
              className="w-4 h-4 rounded border border-gray-400"
            />
            Usuarios recurrentes
          </label>
        </div>
      </div>
      {/* Layout responsivo: limitamos la cantidad de cards por fila para evitar que se achiquen demasiado */}
      <div className="grid gap-6 grid-cols-[repeat(auto-fit,minmax(340px,1fr))]">
        {groupedByLevel.map((pair, idx) => (
          <div
            key={pair[0].title}
            className="flex flex-col items-start mb-4 border border-gray-200 rounded-xl p-4 bg-white"
          >
            <span className="text-lg font-extrabold mb-3" style={montserratStyle}>
              {pair[0].title}
            </span>
            <div className="flex flex-row gap-6 w-full" style={{ marginBottom: '1rem' }}>
              {pair.map((item, i) => (
                <div key={item.subtitle} className="flex-1">
                  <span
                    className="text-base font-semibold text-gray-800 mb-2 block pl-6"
                    style={montserratStyle}
                  >
                    {item.subtitle}
                  </span>
                  <MetricCard
                    title=""
                    value={item.value}
                    isPercent={item.isPercent}
                    prev={item.prev}
                    diff={item.diff}
                    isPositive={item.isPositive}
                    showComparison={false}
                    comparisonMonth=""
                    comparisonYear=""
                    className="h-full"
                  >
                    <span className="text-xs text-gray-600">
                      vs{' '}
                      <span
                        className={
                          item.isPositive
                            ? 'bg-green-100 text-green-700 px-1 rounded'
                            : 'bg-red-100 text-red-700 px-1 rounded'
                        }
                      >
                        {item.prev}%
                      </span>{' '}
                      de la sucursal {compareStoreId === undefined ? 'Todas' : compareStoreId}
                    </span>
                  </MetricCard>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ActiveUsersByLevel
